import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { GetClaimCompareExportRepository } from '../claim-charting-repository';
import type { ClaimFeatureItem, CompareFeatureItem, CompareItem, ExcelParams, FeatureItem } from '../../types';
import { I18nService } from '@analytics/shared/services/i18n.interface';
import type { ExportExcelService } from '../export-excel-service';
import { OpenService } from '@analytics/shared/services/open-service';

@injectable()
export class ExportExcelServiceImpl implements ExportExcelService {
    @inject(GetClaimCompareExportRepository)
    private _getClaimCompareExportRepository!: GetClaimCompareExportRepository;
    @inject(OpenService)
    private _openService!: OpenService;
    constructor(
        @inject(I18nService)
        private _i18n: I18nService,
    ) {}

    private _exportFeatureHeadConfig() {
        return {
            name: {
                key: 'claims',
                i18nValue: this._i18n.t('aiClaim.Claims'),
            },
            value: {
                key: 'features',
                i18nValue: this._i18n.t('aiClaim.Features'),
            },
            trans: {
                key: 'translates',
                i18nValue: this._i18n.t('patent-view.Not translated'),
                transRow: true,
            },
        };
    }

    private _exportCompareHeadConfig(claimPN, descPN) {
        return {
            name: {
                key: 'claims',
                i18nValue: this._i18n.t('aiClaim.Claims'),
            },
            claimValue: {
                key: 'claimFeatures',
                i18nValue: `${this._i18n.t('aiClaim.Features')}${claimPN}`,
            },
            claimTrans: {
                key: 'claimTrans',
                i18nValue: `${this._i18n.t('aiClaim.Features')}${claimPN}${this._i18n.t('patent-view.Not translated')}`,
                transRow: true,
            },
            descValue: {
                key: 'descFeatures',
                i18nValue: `${this._i18n.t('aiClaim.Features')}${descPN}`,
            },
            descTrans: {
                key: 'descTrans',
                i18nValue: `${this._i18n.t('aiClaim.Features')}${descPN}${this._i18n.t('patent-view.Not translated')}`,
                transRow: true,
            },
            isPublic: {
                key: 'isPublic',
                i18nValue: this._i18n.t('aiClaim.PresenceInDesc'),
            },
            reason: {
                key: 'reason',
                i18nValue: this._i18n.t('aiClaim.AIExplanation'),
            },
        };
    }

    private _getHead(config: any, hasTransLang: boolean) {
        const configKeys = Object.keys(config);
        // 如果有翻译就在最后添加一列翻译, name：'translates'表示翻译列
        if (hasTransLang) {
            return configKeys.map(key => config[key].i18nValue);
        } else{
            return configKeys.filter(key => !config[key].transRow).map(key => config[key].i18nValue);
        }
    }

    private _getBody(tableData: ClaimFeatureItem[], getTr: (item: string[]) => string[]) {
        return tableData.flatMap(item => {
            return item.features.map((feature: FeatureItem, index: number) => {
                const { value, trans = '' } = feature;
                const name = index === 0 ? item.name : '';
                return getTr([name, value, trans]);
            });
        });
    }

    private _getTr(item: string[], headLength: number) {
        return item.slice(0, headLength);
    }

    public async exportClaimExcel(tableData: ClaimFeatureItem[], tableInfo: ExcelParams) {
        const head = this._getHead(this._exportFeatureHeadConfig(), tableInfo.hasTransLang);
        const body = this._getBody(tableData, item => this._getTr(item, head.length));
        const params = {
            fileName: `${new Date().getTime()}/${tableInfo.claimPN}-Claim features`,
            charts: [{
                sheetName: 'sheet1',
                head,
                body,
            }],
        };
        const res = await this._getClaimCompareExportRepository.getClaimCompareExport(params);
        if (res) this._openService.open(res, { target: '_blank' });
    }

    private _removeBTags(value: string): string {
        return value.replace(/<\/?b>/gi, '');
    }

    private _getBodyCompare(data: CompareFeatureItem[], getTr: (item: string[]) => string[]) {
        return data.flatMap(item => {
            return item.compareFeatures.map((feature: CompareItem, index: number) => {
                const { claimVal, claimTrans = '', compareVal ='', compareTrans = '', isPublic = '', reason ='' } = feature;
                const filteredClaimVal = this._removeBTags(claimVal);
                const filteredClaimTrans = this._removeBTags(claimTrans);
                const filteredCompareVal = this._removeBTags(compareVal);
                const filteredCompareTrans = this._removeBTags(compareTrans);
                const isPublicText = isPublic ? this._i18n.t('aiClaim.Yes' ) : this._i18n.t('aiClaim.No' );
                const name = index === 0 ? item.name : '';
                return getTr([name, filteredClaimVal, filteredClaimTrans, filteredCompareVal, filteredCompareTrans, isPublicText, reason]);
            });
        });
    }

    private _getTrCompare(item: string[], headLength: number) {
        // 不带翻译是5列,带翻译是7列
        if (headLength === 5) {
            const [name, claimVal, , compareVal, , isPublic, reason] = item;
            return [name, claimVal, compareVal, isPublic, reason];
        }
        return item.slice(0, headLength);
    }

    public async exportCompareExcel(data: CompareFeatureItem[], tableInfo: ExcelParams) {
        const head = this._getHead(this._exportCompareHeadConfig(tableInfo.claimPN, tableInfo.descPN), tableInfo.hasTransLang);
        const body = this._getBodyCompare(data, item => this._getTrCompare(item, head.length));
        const params = {
            fileName: `${new Date().getTime()}/${tableInfo.claimPN}-${tableInfo.descPN}-Claim chart`,
            charts: [{
                sheetName: 'sheet1',
                head,
                body,
            }],
        };
        const res = await this._getClaimCompareExportRepository.getClaimCompareExport(params);
        if (res) this._openService.open(res, { target: '_blank' });
    }
}
